import { ThreeEvent } from '@react-three/fiber';
import { BaseTool } from '../BaseTool';
import { ClipboardManager } from '../../core/ClipboardManager';
import * as THREE from 'three';

/**
 * Paste Tool
 * 
 * Allows placing elements from the clipboard.
 * Behaves like placing a component or group.
 */
export class PasteToolClass extends BaseTool {
    id = 'PASTE';
    name = 'Paste';
    cursor = 'copy';

    private previewElements: any[] = [];
    private originalCenter: THREE.Vector3 = new THREE.Vector3();

    activate(context: any) {
        super.activate(context);

        if (!ClipboardManager.hasContent) {
            console.warn('Paste: Clipboard empty.');
            this.deactivate();
            this.context?.appStore.setActiveTool('select');
            return;
        }

        // Prepare preview
        // We need to calculate the bounding center of the clipboard content to use as handle
        // For simplicity, we'll just take the first element's position or 0,0,0 if not available
        // A robust system would calculate the centroid of all elements.

        // Mock centroid calculation
        this.originalCenter.set(0, 0, 0);
        // In a real implementation we would iterate the clipboard buffer to find the center.
    }

    onPointerDown(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;
        if (event.button !== 0) return;

        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);
            this.pasteElements(snapped);

            // Single paste or multiple? Standard is single then exit, or continuous.
            // Let's do single for now.
            this.deactivate();
            this.context.appStore.setActiveTool('select');
        }
    }

    onPointerMove(event: ThreeEvent<PointerEvent>): void {
        if (!this.context) return;

        const point = this.getIntersection(event);
        if (point) {
            const snapped = this.snapToGrid(point);

            // Preview would be complex (rendering ghost elements).
            // For now, we reuse 'component' preview type or 'selectionBox' type if possible,
            // or just a simple marker.
            this.context.appStore.setToolPreview({
                type: 'point',
                position: snapped,
                color: '#00FF00'
            });
        }
    }

    onPointerUp(): void { }

    private pasteElements(targetPoint: THREE.Vector3) {
        if (!this.context) return;

        // Calculate offset from original center to target point
        // If we don't have original center, we might just assume 0,0,0 was the origin.

        const offset = {
            x: targetPoint.x,
            y: targetPoint.y,
            z: targetPoint.z
        };

        const newElements = ClipboardManager.paste(offset);

        newElements.forEach(el => {
            this.context!.bimStore.addElement(el);
        });

        // Select pasted elements
        const newIds = newElements.map(e => e.id);
        this.context.appStore.select(newIds);

        console.log(`Paste: Placed ${newElements.length} elements at`, targetPoint);
    }
}
